/**
 * Super Admin → Billing Emails (PR 0 kill-switch UI)
 *
 * Arms / disarms the billing-email kill-switch. Two independent
 * toggles are surfaced:
 *
 *   1. GLOBAL env var `SUPPRESS_BILLING_EMAILS=1` — set on the
 *      backend deployment. Displayed here as read-only so ops knows
 *      whether the master switch is on. When on, per-tenant toggles
 *      have NO additional effect.
 *
 *   2. PER-TENANT flag stored in `tenant_settings.billing_email_suppression`.
 *      Toggle-able from this page. Takes effect within a request
 *      cycle (backend invalidates its 15s cache on toggle).
 *
 * Backend endpoints:
 *   GET  /api/super-admin/billing/email-suppression-list
 *   POST /api/super-admin/billing/email-suppression-toggle
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MailX, Mail, ShieldAlert, Loader2, RefreshCw, Info, CheckCircle2, XCircle,
} from 'lucide-react';

const API = (
  typeof window !== 'undefined' && window.location
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL
) + '/api';

const authHeader = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CATEGORIES = [
  'billing_receipt',
  'billing_autopay_success',
  'billing_autopay_failure',
  'billing_invoice_reminder',
  'billing_overdue_reminder',
  'billing_ach_settled',
  'billing_refund_receipt',
  'billing_payment_link',
  'billing_summary',
];

export default function SuperAdminBillingEmails() {
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [data, setData] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [reasonById, setReasonById] = useState({});

  const load = async (isReload = false) => {
    if (isReload) setReloading(true); else setLoading(true);
    try {
      const res = await axios.get(
        `${API}/super-admin/billing/email-suppression-list`,
        { headers: authHeader() },
      );
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load suppression state', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (tenant, nextOn) => {
    const reason = reasonById[tenant.tenant_id] || null;
    if (nextOn && !reason) {
      toast.warning('Add a short reason before muting a tenant', {
        description: 'This lands in audit_logs so we know why the switch was flipped.',
      });
      return;
    }
    setTogglingId(tenant.tenant_id);
    try {
      const res = await axios.post(
        `${API}/super-admin/billing/email-suppression-toggle`,
        {
          tenant_id: tenant.tenant_id,
          suppress_billing_emails: nextOn,
          reason,
        },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
      );
      toast.success(
        nextOn
          ? `Muted billing emails for ${tenant.tenant_name}`
          : `Re-enabled billing emails for ${tenant.tenant_name}`,
        { description: `at ${res.data.updated_at}` },
      );
      // Reset the reason input on this row and reload the list.
      setReasonById((prev) => ({ ...prev, [tenant.tenant_id]: '' }));
      await load(true);
    } catch (err) {
      toast.error('Toggle failed', {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 text-slate-600"
        data-testid="billing-emails-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading suppression state…
      </div>
    );
  }
  if (!data) return null;

  const globalOn = data.global_env_kill_switch;

  return (
    <div className="max-w-5xl space-y-6" data-testid="billing-emails-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <MailX className="h-6 w-6 text-amber-600" />
          Billing Email Kill-Switch
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Mute parent-facing billing emails during the Billing Overhaul.
          Governs receipts, autopay confirmations, invoice reminders, ACH
          notices, refund receipts, and payment-link emails. Non-billing
          flows (auth, trials, team notifications) are never affected.
        </p>
      </div>

      {/* Global env kill-switch banner */}
      <div
        className={
          'rounded-md border p-4 flex items-start gap-3 '
          + (globalOn
            ? 'border-amber-300 bg-amber-50'
            : 'border-slate-200 bg-white')
        }
        data-testid="global-switch-banner"
      >
        <ShieldAlert
          className={
            'h-5 w-5 mt-0.5 shrink-0 '
            + (globalOn ? 'text-amber-600' : 'text-slate-400')
          }
        />
        <div className="flex-1 text-sm">
          <div className="font-medium text-slate-900">
            Global env kill-switch — <span className="font-mono">SUPPRESS_BILLING_EMAILS</span>
          </div>
          <div className="text-slate-600 mt-1">
            {globalOn ? (
              <>
                <span className="font-semibold text-amber-800">
                  ON — all billing emails muted across every tenant.
                </span>
                {' '}
                Per-tenant toggles below have no additional effect while
                this is on.
              </>
            ) : (
              <>
                Not set. Per-tenant toggles below control suppression
                individually. To mute globally, set{' '}
                <span className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">
                  SUPPRESS_BILLING_EMAILS=1
                </span>
                {' '}in the backend deployment env and redeploy.
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={reloading}
          className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 disabled:opacity-50"
          data-testid="refresh-suppression-state-btn"
        >
          <RefreshCw className={`h-3 w-3 ${reloading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Categories governed */}
      <details className="rounded-md border border-slate-200 bg-white p-4 text-sm">
        <summary className="cursor-pointer flex items-center gap-2 font-medium text-slate-800">
          <Info className="h-4 w-4 text-slate-500" />
          Categories governed by this switch
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-1 text-slate-600 text-xs font-mono">
          {CATEGORIES.map((c) => <div key={c}>{c}</div>)}
        </div>
      </details>

      {/* Per-tenant table */}
      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-medium text-slate-900">
            Per-tenant switches
            <span className="ml-2 text-xs text-slate-500">
              ({data.tenants.length} tenants)
            </span>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {data.tenants.map((t) => {
            const isBusy = togglingId === t.tenant_id;
            const emailsFire = t.billing_emails_would_send;
            const rowReason = reasonById[t.tenant_id] || '';
            return (
              <div
                key={t.tenant_id}
                className="px-4 py-3 flex items-center gap-4"
                data-testid={`tenant-suppression-row-${t.tenant_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {t.tenant_name || t.tenant_slug || t.tenant_id}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {t.tenant_slug ? `${t.tenant_slug} · ` : ''}
                    <span className="font-mono">{t.tenant_id}</span>
                  </div>
                  {t.last_toggle?.at && (
                    <div className="text-xs text-slate-400 mt-0.5">
                      Last change: {new Date(t.last_toggle.at).toLocaleString()}
                      {t.last_toggle.by ? ` by ${t.last_toggle.by}` : ''}
                      {t.last_toggle.reason ? ` — "${t.last_toggle.reason}"` : ''}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {emailsFire ? (
                    <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
                      <Mail className="h-3 w-3" />
                      Emails firing
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
                      <MailX className="h-3 w-3" />
                      Muted
                    </span>
                  )}
                </div>
                {!t.tenant_kill_switch && (
                  <input
                    type="text"
                    value={rowReason}
                    onChange={(e) => setReasonById((prev) => ({
                      ...prev, [t.tenant_id]: e.target.value,
                    }))}
                    placeholder="Reason (required to mute)"
                    className="text-xs border border-slate-200 rounded px-2 py-1 w-48 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    disabled={isBusy}
                    data-testid={`tenant-suppression-reason-${t.tenant_id}`}
                  />
                )}
                <button
                  type="button"
                  onClick={() => handleToggle(t, !t.tenant_kill_switch)}
                  disabled={isBusy || globalOn}
                  title={
                    globalOn
                      ? 'Disabled while global env kill-switch is on'
                      : t.tenant_kill_switch
                        ? 'Un-mute this tenant'
                        : 'Mute this tenant'
                  }
                  className={
                    'text-xs px-3 py-1.5 rounded font-medium border transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed '
                    + (t.tenant_kill_switch
                      ? 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'
                      : 'border-amber-600 text-amber-700 hover:bg-amber-50')
                  }
                  data-testid={`tenant-suppression-toggle-${t.tenant_id}`}
                >
                  {isBusy ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : t.tenant_kill_switch ? (
                    <CheckCircle2 className="h-3 w-3" />
                  ) : (
                    <XCircle className="h-3 w-3" />
                  )}
                  {t.tenant_kill_switch ? 'Un-mute' : 'Mute billing emails'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
